// src/api/client.js — drop this into the frontend src/api/ folder

const BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

const PUBLIC_AUTH_PATHS = /^\/auth\/(signin|signup|request-otp|verify-otp|recover-pin|check-tag|check-signup)/;

// ── Token storage ─────────────────────────────────────────────────
export const getToken = () => localStorage.getItem("pp_access_token");
export const getRefresh = () => localStorage.getItem("pp_refresh_token");
export const setTokens = ({ accessToken, refreshToken }) => {
  localStorage.setItem("pp_access_token", accessToken);
  if (refreshToken) localStorage.setItem("pp_refresh_token", refreshToken);
};
export const clearTokens = () => {
  localStorage.removeItem("pp_access_token");
  localStorage.removeItem("pp_refresh_token");
};

// ── Core fetch with auto-refresh ──────────────────────────────────
const api = async (method, path, body = null, retry = true) => {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method: method.toUpperCase(),
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Auto-refresh on 401 for authenticated requests only (not login/signup failures)
  if (res.status === 401 && retry && token && !PUBLIC_AUTH_PATHS.test(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return api(method, path, body, false);
    clearTokens();
    window.location.reload();
    throw new Error("Session expired");
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = Array.isArray(data.details) ? data.details.join(". ") : null;
    const err = new Error(detail || data.error || `Request failed: ${res.status}`);
    err.code = data.code;
    err.apiDetails = data.details;
    throw err;
  }
  return data;
};

const refreshAccessToken = async () => {
  const refreshToken = getRefresh();
  if (!refreshToken) return false;
  try {
    const data = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    }).then(r => r.json());
    if (data.accessToken) { setTokens(data); return true; }
    return false;
  } catch { return false; }
};

// ── Auth ──────────────────────────────────────────────────────────
export const requestOTP = (phone, purpose, email) =>
  api("post", "/auth/request-otp", { phone, purpose, ...(email ? { email } : {}) });
export const verifyOTP = (phone, code, purpose) => api("post", "/auth/verify-otp", { phone, code, purpose });
/** Public tag check during signup (no auth required). */
export const checkTagAvailability = (tag) =>
  api("get", `/auth/check-tag?tag=${encodeURIComponent(tag)}`);
/** Public phone/email availability check during signup (no auth required). */
export const checkSignupAvailability = (phone, email) => {
  const params = new URLSearchParams();
  if (phone) params.set("phone", phone);
  if (email) params.set("email", email);
  return api("get", `/auth/check-signup?${params}`);
};
export const signin = async (phone, pin, fcmToken) => {
  const body = { phone, pin };
  if (fcmToken) body.fcmToken = fcmToken;
  const data = await api("post", "/auth/signin", body);
  setTokens(data);
  return data;
};
export const signup = async (payload) => {
  const data = await api("post", "/auth/signup", payload);
  setTokens(data);
  return data;
};
export const recoverPin = (phone, newPin, stepToken) =>
  api("post", "/auth/recover-pin", { phone, newPin, stepToken });
export const initiateSafeHavenIdentity = (identityNumber, identityType = "BVN") =>
  api("post", "/wallet/safehaven-identity/initiate", { identityNumber, identityType });
export const validateSafeHavenIdentity = (reference, otp, identityType = "BVN") =>
  api("post", "/wallet/safehaven-identity/validate", { reference, otp, identityType });

/** Map initiate response to a user-visible error (Safe Haven BVN → OTP). */
export const formatSafeHavenInitError = (init) => {
  if (!init) return "Could not start verification. Try again later.";
  if (init.error) {
    if (/ip not allowed/i.test(init.error) || init.code === "SAFEHAVEN_IP_NOT_ALLOWED") {
      return "PendPay cannot reach Safe Haven yet — your hosting IP (Railway) must be whitelisted by Safe Haven for Live API access. Email Safe Haven support and ask them to whitelist your server egress IP.";
    }
    if (/identity response is incomplete/i.test(init.error)) {
      return "Verification was not completed. Enter your BVN again — we will send a new OTP to your phone.";
    }
    if (/unable to fetch record/i.test(init.error)) {
      return "Safe Haven could not look up this BVN. Double-check the 11-digit number matches your bank records. If correct, ensure your settlement account (ending in the digits Safe Haven debits for verification) has at least ₦100, or contact Safe Haven support.";
    }
    return init.error;
  }
  if (init.identityReset && init.otpRequired && init.reference) {
    return null; // caller should advance to OTP step
  }
  if (init.alreadyLinked && init.virtualAccountPending) {
    return "Identity verified but your bank account is still being set up. Try again in a few minutes.";
  }
  if (init.reason === "already_linked") return "Identity already linked to your account.";
  if (init.skipped) return "Bank verification is unavailable in this environment.";
  return "Could not start verification. Try again later.";
};
export const signout = async () => {
  const refreshToken = getRefresh();
  try {
    if (refreshToken) await api("post", "/auth/signout", { refreshToken }, false);
  } catch {
    /* clear local session even if revoke fails */
  }
  clearTokens();
};

// ── Users ─────────────────────────────────────────────────────────
export const getMe = () => api("get", "/users/me");
export const checkTag = (tag) => api("get", `/users/check-tag?tag=${tag}`);
export const searchUsers = (q) => api("get", `/users/search?q=${encodeURIComponent(q)}`);
export const changePin = (currentPin, newPin) =>
  api("post", "/users/change-pin", { currentPin, newPin });
export const getProfileDisputes = () => api("get", "/users/disputes");
export const getNotificationPreferences = () => api("get", "/users/notification-preferences");
export const updateNotificationPreferences = (preferences) =>
  api("patch", "/users/notification-preferences", preferences);
export const revokeSession = (sessionId) => api("delete", `/users/sessions/${sessionId}`);
export const freezeAccount = () => api("post", "/users/freeze");
export const getCloseAccountEligibility = () => api("get", "/users/close-account/eligibility");
export const closeAccount = (pin, stepToken) => api("post", "/users/close-account", { pin, stepToken });
export const getSessions = async () => {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  const refreshToken = getRefresh();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (refreshToken) headers["X-Refresh-Token"] = refreshToken;
  const res = await fetch(`${BASE}/users/sessions`, { method: "GET", headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
};
export const getNotifications = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return api("get", `/users/notifications${q ? `?${q}` : ""}`);
};
export const getNotificationUnreadCount = () => api("get", "/users/notifications/unread-count");
export const createNotificationStreamToken = () => api("post", "/users/notifications/stream-token");
export const markNotificationRead = (id) => api("patch", `/users/notifications/${id}/read`);
export const markAllNotificationsRead = () => api("patch", "/users/notifications/read-all");
export const updateProfile = (data) => api("patch", "/users/me", data);

// ── Invitations ───────────────────────────────────────────────────
export const sendInvitation = (data) => api("post", "/users/invitations", data);
export const getInvitations = (limit = 20) =>
  api("get", `/users/invitations?limit=${limit}`);
export const resolveInvitationLink = async (token) => {
  const headers = { "Content-Type": "application/json" };
  const accessToken = getToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${BASE}/invitations/${encodeURIComponent(token)}`, { headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 404) {
      return { status: "not_found", scenario: "not_found", error: data.error };
    }
    throw new Error(data.error || "Failed to load invitation");
  }
  return data;
};
export const acceptInvitationLink = (token) =>
  api("post", `/users/invitations/${encodeURIComponent(token)}/accept`);
/** @deprecated use resolveInvitationLink */
export const getInvitationPreview = (token) => resolveInvitationLink(token);

// ── Wallet ────────────────────────────────────────────────────────
export const getWallet = () => api("get", "/wallet");
export const getWalletTransactions = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api("get", `/wallet/transactions${qs ? `?${qs}` : ""}`);
};
export const withdraw = (data) => api("post", "/wallet/withdraw", data);
export const initiateWithdraw = (data) => api("post", "/wallet/withdraw/initiate", data);
export const confirmWithdraw = (data) => api("post", "/wallet/withdraw/confirm", data);
export const transferToUser = (data) => api("post", "/wallet/transfer", data);
export const verifyBankAccount = (bankCode, accountNumber) =>
  api("post", "/wallet/verify-bank", { bankCode, accountNumber });
export const getTopupReference = (amountNaira) =>
  api("post", "/wallet/topup-reference", { amountNaira });
export const initializeWalletTopup = (amountNaira, paymentMethod) =>
  api("post", "/wallet/topup/initialize-payment", { amountNaira, paymentMethod });
export const confirmWalletTopup = (reference) =>
  api("post", "/wallet/topup/confirm-payment", { reference });

// ── Deals ─────────────────────────────────────────────────────────
export const analyzeContract = (contractText) =>
  api("post", "/deals/analyze-contract", { contractText });

export const createDeal = async (data, contractFile = null) => {
  if (!contractFile) return api("post", "/deals", data);

  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const form = new FormData();
  for (const [key, value] of Object.entries(data)) {
    if (value == null) continue;
    if (key === "milestones") form.append(key, JSON.stringify(value));
    else form.append(key, String(value));
  }
  form.append("contractFile", contractFile);

  const res = await fetch(`${BASE}/deals`, {
    method: "POST",
    headers,
    body: form,
  });

  if (res.status === 401 && token) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return createDeal(data, contractFile);
    clearTokens();
    window.location.reload();
    throw new Error("Session expired");
  }

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = Array.isArray(payload.details) ? payload.details.join(". ") : null;
    throw new Error(detail || payload.error || `Request failed: ${res.status}`);
  }
  return payload;
};
export const initializeDealPayment = (id) => api("post", `/deals/${id}/initialize-payment`);
export const confirmDealPayment = (id, reference) =>
  api("post", `/deals/${id}/confirm-payment`, { reference });
export const initializeDealCheckout = (id, platform = "web") =>
  api("post", `/deals/${id}/initialize-checkout`, { platform });
export const confirmDealCheckout = (id, referenceCode) =>
  api("post", `/deals/${id}/confirm-checkout`, { referenceCode });
export const checkDealInstantTransfer = (id) =>
  api("post", `/deals/${id}/check-instant-transfer`);
export const getDeals = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api("get", `/deals${qs ? `?${qs}` : ""}`);
};
export const getDeal = (id) => api("get", `/deals/${id}`);
export const getDealTermQueries = (id) => api("get", `/deals/${id}/term-queries`);
export const postDealTermQuery = (id, body) => api("post", `/deals/${id}/term-queries`, body);
export const respondDealTermQuery = (id, queryId, body) =>
  api("post", `/deals/${id}/term-queries/${queryId}/respond`, body);
export const acceptDeal = (id) => api("post", `/deals/${id}/accept`);
export const declineDeal = (id) => api("post", `/deals/${id}/decline`);
export const cancelDeal = (id) => api("post", `/deals/${id}/cancel`);
export const claimMilestone = (id, note) => api("post", `/deals/${id}/claim-milestone`, { note });
export const approveMilestone = (id) => api("post", `/deals/${id}/approve-milestone`);
export const partialReleaseMilestone = (id, body) => api("post", `/deals/${id}/partial-release-milestone`, body);
export const rejectMilestone = (id, reason) => api("post", `/deals/${id}/reject-milestone`, { reason });
export const raiseDeal = (id, reason, evidenceUrls) =>
  api("post", `/deals/${id}/dispute`, { reason, evidenceUrls });
export const releaseDespiteDispute = (id) => api("post", `/deals/${id}/dispute/release-anyway`);

export const getScopeMessages = (dealId) => api("get", `/deals/${dealId}/scope-messages`);
export const postScopeMessage = (dealId, body) => api("post", `/deals/${dealId}/scope-messages`, body);
export const respondAmountProposal = (dealId, messageId, action) =>
  api("post", `/deals/${dealId}/scope-messages/${messageId}/respond-amount`, { action });

/** Multipart upload — returns public URLs for dispute evidence. */
export const uploadDisputeAttachments = async (dealId, files) => {
  if (!files?.length) return [];
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}/deals/${dealId}/dispute-attachments`, {
    method: "POST",
    headers,
    body: (() => {
      const form = new FormData();
      for (const f of files) form.append("files", f);
      return form;
    })(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
  return data.urls || [];
};

export const appendDisputeEvidence = (dealId, body) =>
  api("post", `/deals/${dealId}/dispute/append-evidence`, body);

export default { setTokens, clearTokens, getToken };

// ── App config (cached) ───────────────────────────────────────────
let configCache = null;
export const getAppConfig = async () => {
  if (configCache) return configCache;
  const res = await fetch(`${BASE}/config`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to load app config");
  configCache = data;
  return data;
};

export const getFrequentContacts = (limit) => {
  const q = limit != null ? `?limit=${encodeURIComponent(limit)}` : "";
  return api("get", `/users/frequent-contacts${q}`);
};
export const getDealAnalytics = (period = "Month") =>
  api("get", `/deals/analytics?period=${encodeURIComponent(period)}`);
export const getLinkedBanks = () => api("get", "/wallet/linked-banks");
export const linkBankAccount = (data) => api("post", "/wallet/linked-banks", data);
export const unlinkBankAccount = (id) => api("delete", `/wallet/linked-banks/${id}`);

// ── Events / tickets ──────────────────────────────────────────────
export const listEvents = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.tag) qs.set("tag", params.tag);
  if (params.thisWeek) qs.set("thisWeek", "1");
  if (params.upcoming) qs.set("upcoming", "1");
  if (params.sort) qs.set("sort", params.sort);
  if (params.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return api("get", `/events${q ? `?${q}` : ""}`);
};
export const getEvent = (id) => api("get", `/events/${id}`);
export const listMyEvents = () => api("get", "/events/mine");
export const createEvent = (data) => api("post", "/events", data);
export const updateEvent = (id, data) => api("patch", `/events/${id}`, data);

/** Multipart upload — returns public URL for an event cover/banner image. */
export const uploadEventCover = async (file) => {
  if (!file) throw new Error("Cover image is required");
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const form = new FormData();
  form.append("cover", file);
  const res = await fetch(`${BASE}/events/cover`, {
    method: "POST",
    headers,
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
  return data.coverImageUrl;
};
export const createTicketOrder = (eventId, items, attribution = null) =>
  api("post", `/events/${eventId}/orders`, { items, ...(attribution || {}) });
export const createGuestTicketOrder = (eventId, { buyerName, buyerEmail, buyerPhone, items, attribution }) =>
  api("post", `/events/${eventId}/orders/guest`, {
    buyerName,
    buyerEmail,
    ...(buyerPhone ? { buyerPhone } : {}),
    items,
    ...(attribution || {}),
  });
export const registerFreeTickets = (eventId, { buyerName, buyerEmail, buyerPhone, items, idempotencyKey, attribution }) =>
  api("post", `/events/${eventId}/register`, {
    ...(buyerName ? { buyerName } : {}),
    ...(buyerEmail ? { buyerEmail } : {}),
    ...(buyerPhone ? { buyerPhone } : {}),
    items,
    idempotencyKey,
    ...(attribution || {}),
  });
export const getEventAnalytics = (eventId, params = {}) => {
  const qs = new URLSearchParams();
  if (params.range) qs.set("range", params.range);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const q = qs.toString();
  return api("get", `/events/${eventId}/analytics${q ? `?${q}` : ""}`);
};
export const collectEventAnalytics = (eventId, body) => {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 4000) : null;
  return fetch(`${BASE}/events/${eventId}/analytics/collect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: controller ? controller.signal : undefined,
  })
    .catch(() => {})
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
};
export const getTicketOrder = (id, guestToken = null) =>
  api("get", `/orders/${id}${guestToken ? `?token=${encodeURIComponent(guestToken)}` : ""}`);
export const quoteTicketOrder = (id, method, guestToken = null) =>
  api("get", `/orders/${id}/quote?method=${encodeURIComponent(method)}${guestToken ? `&token=${encodeURIComponent(guestToken)}` : ""}`);
export const payTicketOrderWallet = (id) => api("post", `/orders/${id}/pay/wallet`);
export const payTicketOrderBank = (id, guestToken = null) =>
  api("post", `/orders/${id}/pay/bank`, guestToken ? { token: guestToken } : null);
export const checkTicketOrderBank = (id, guestToken = null) =>
  api("post", `/orders/${id}/pay/bank/check`, guestToken ? { token: guestToken } : null);
export const initializeTicketCardPayment = (id, guestToken = null) =>
  api(
    "post",
    `/orders/${id}/pay/card/initialize`,
    guestToken ? { token: guestToken } : null
  );
export const confirmTicketCardPayment = (id, reference, guestToken = null) =>
  api("post", `/orders/${id}/pay/card/confirm`, {
    reference,
    ...(guestToken ? { token: guestToken } : {}),
  });
export const listMyTickets = () => api("get", "/tickets/mine");
export const getTicket = (id) => api("get", `/tickets/${id}`);
export const checkInTicket = (qrToken, eventId) =>
  api("post", "/tickets/check-in", {
    qrToken,
    ...(eventId ? { eventId } : {}),
  });
export const getTicketPassAvailability = (orderId) =>
  api("get", `/orders/${orderId}/passes/availability`);
export const getGoogleWalletPassUrl = (orderId) =>
  api("get", `/orders/${orderId}/passes/google`);

/** Download Apple .pkpass for an order (returns blob URL). */
export const downloadAppleWalletPass = async (orderId) => {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}/orders/${orderId}/passes/apple`, { headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Apple Wallet pass unavailable");
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};
